import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { Home, ArrowLeft, User, Mail, CreditCard, Phone, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import AppButton from '@/components/ui/AppButton';
import ThemeToggle from '@/components/ThemeToggle';

const PerfilUsuario = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) return null;

  return (
    <div className="min-h-screen gradient-background">
      {/* Header Premium */}
      <div className="bg-card/50 dark:bg-card/40 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-primary/10 flex items-center justify-center border border-primary/20 shrink-0">
                <User className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              </div>
              <h1 className="text-sm sm:text-xl md:text-2xl font-black tracking-tight text-foreground uppercase truncate">Mi Perfil</h1>
            </div>
            
            <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
              <ThemeToggle />
              <AppButton 
                variant="secondary" 
                size="sm"
                onClick={() => navigate('/usuario')} 
                icon={ArrowLeft}
                className="h-9 px-2 sm:px-4"
                data-testid="back-btn"
              >
                <span className="hidden sm:inline">Volver</span>
              </AppButton>
              <AppButton 
                variant="secondary" 
                size="sm"
                onClick={() => navigate('/')} 
                icon={Home}
                className="h-9 px-2 sm:px-4"
                data-testid="home-btn"
              >
                <span className="hidden sm:inline">Inicio</span>
              </AppButton>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <Card className="overflow-hidden border-none shadow-2xl bg-card/50 dark:bg-white/5 backdrop-blur-2xl ring-1 ring-white/10 relative">
          {/* Subtle Glow Background */}
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-primary/5 blur-[100px] rounded-full"></div>
          
          <CardHeader className="border-b border-white/5 pb-8">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-6 h-6 text-primary" />
              <CardTitle className="text-xl font-black uppercase tracking-tight text-foreground">Información Personal</CardTitle>
            </div>
          </CardHeader>
          
          <CardContent className="pt-8">
            <div className="grid gap-8">
              {/* Profile Card Items */}
              {[
                { label: 'Nombre Completo', value: user.name, icon: User, color: 'blue' },
                { label: 'Correo Electrónico', value: user.email, icon: Mail, color: 'green' },
                { label: 'Número de Cédula', value: user.cedula, icon: CreditCard, color: 'purple', condition: user.cedula },
                { label: 'Número de Celular', value: user.celular, icon: Phone, color: 'orange', condition: user.celular },
                { label: 'Rol del Sistema', value: user.role, icon: ShieldCheck, color: 'slate', isTag: true }
              ].map((item, index) => {
                if (item.condition === false) return null;
                
                const colorClasses = {
                  blue: 'bg-blue-500/10 text-blue-600',
                  green: 'bg-green-500/10 text-green-600',
                  purple: 'bg-purple-500/10 text-purple-600',
                  orange: 'bg-orange-500/10 text-orange-600',
                  slate: 'bg-slate-500/10 text-slate-600'
                };

                return (
                  <div key={index} className="flex items-start gap-6 group">
                    <div className={cn(
                      "p-4 rounded-2xl transition-all duration-300 group-hover:scale-110 shadow-inner ring-1 ring-black/5 dark:ring-white/10",
                      colorClasses[item.color] || 'bg-primary/10 text-primary'
                    )}>
                      <item.icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1 border-b border-white/5 pb-6">
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground mb-1">{item.label}</p>
                      <p className={cn(
                        "text-lg font-bold text-foreground",
                        item.isTag && "capitalize inline-block bg-primary/20 text-primary px-3 py-1 rounded-lg text-sm"
                      )}>
                        {item.value}
                      </p>
                    </div>
                  </div>
                );
              })}

              {!user.datos_completos && (
                <div className="mt-4 p-6 bg-yellow-500/10 border border-yellow-500/20 rounded-3xl backdrop-blur-md relative overflow-hidden group">
                  <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-125 transition-transform duration-500">
                    <ShieldCheck className="w-16 h-16 text-yellow-500" />
                  </div>
                  <div className="relative z-10">
                    <h4 className="text-yellow-600 dark:text-yellow-500 font-black uppercase tracking-widest text-xs mb-2">¡Llamada a la Acción!</h4>
                    <p className="text-sm text-foreground/80 font-medium max-w-md">
                      Tu perfil está incompleto. Por favor completa tus datos para poder participar en los sorteos y disfrutar de la experiencia completa.
                    </p>
                    <AppButton 
                      variant="primary"
                      size="sm"
                      className="mt-4"
                      onClick={() => navigate('/completar-datos')}
                    >
                      Completar Mis Datos
                    </AppButton>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PerfilUsuario;
